/**
 * The reporting periods every report offers, as calendar day ranges.
 *
 * One module owns the arithmetic because the alternative already started: the
 * funnel screen carried its own `previousMonth` and the analytics screen its
 * own `trailingSevenDays`, so two reports labelled "last month" and "last 7
 * days" could drift apart without either file looking wrong. Everything here
 * returns the same `CalendarDateRange` the picker and the endpoints already
 * speak, so a preset and a hand-picked range are indistinguishable downstream.
 *
 * **A week runs Saturday to Friday.** That is Finance's week, and a report that
 * disagrees with Finance is worse than no report. The convention is a default,
 * not a constant — every function here takes a `weekStart`, so moving the
 * company to another day is a change of argument rather than a rewrite. See
 * `docs/decisions/0011-a-reporting-week-runs-saturday-to-friday.md`.
 *
 * Every calculation is local-time, for the reason `calendar-date.ts` gives: a
 * calendar day has no zone, and routing one through UTC moves it by a day for
 * half the world. `Date` is used only as arithmetic scratch and never escapes.
 */

import { toCalendarDate, type CalendarDateRange } from './calendar-date.ts'

/**
 * A day of the week, as `Date.getDay()` numbers it: 0 is Sunday, 6 is Saturday.
 */
export type WeekStart = 0 | 1 | 2 | 3 | 4 | 5 | 6

/**
 * The day Finance's week begins — Saturday.
 *
 * Exported rather than inlined so a reader can find every place the convention
 * is applied, and so changing it is one edit with a test that names it.
 */
export const FINANCE_WEEK_START: WeekStart = 6

/** Every `WeekStart`, Sunday first, for a control that offers the choice. */
export const WEEK_STARTS: readonly WeekStart[] = Object.freeze([
  0, 1, 2, 3, 4, 5, 6
])

/** How each `WeekStart` is named on screen. */
export const WEEK_START_LABELS: Readonly<Record<WeekStart, string>> =
  Object.freeze({
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday'
  })

/**
 * Read a week start back off a control or a URL.
 *
 * Anything that is not one of the seven is rejected rather than coerced: a
 * report that silently falls back to Sunday when asked for Saturday is the
 * disagreement with Finance this module exists to prevent.
 *
 * @param value - A candidate week start, from anywhere untrusted.
 * @returns The week start, or null when the value names none.
 */
export function parseWeekStart(value: unknown): WeekStart | null {
  // `Number('')` and `Number('  ')` are both 0, so an absent or blank URL
  // parameter would arrive here as Sunday. Blank means "nothing was chosen",
  // and the caller's default has to win.
  if (typeof value === 'string' && value.trim() === '') {
    return null
  }
  const day = typeof value === 'string' ? Number(value) : value
  if (typeof day !== 'number' || !Number.isInteger(day)) {
    return null
  }

  // Matched against the list rather than range-checked and asserted, so the
  // narrowing is the lookup itself and no cast can outlive a changed union.
  return WEEK_STARTS.find(known => known === day) ?? null
}

/**
 * A copy of `value` moved by `days`.
 *
 * Copied rather than mutated because every caller here holds a `Date` it still
 * needs; `setDate` on a shared reference is how one preset silently shifts
 * another. Month and year overflow is `Date`'s own and is correct: 31 January
 * plus one day is 1 February.
 *
 * @param value - The day to move from.
 * @param days - How many days to move, negative to go back.
 * @returns The moved day.
 */
function addDays(value: Date, days: number): Date {
  const moved = new Date(value)
  moved.setDate(moved.getDate() + days)

  return moved
}

/**
 * Midnight on the first day of the week `value` falls in.
 *
 * @param value - Any day.
 * @param weekStart - The day the week begins.
 * @returns The first day of that week.
 */
export function startOfWeek(value: Date, weekStart: WeekStart): Date {
  // `+ 7` before the modulo because `getDay() - weekStart` is negative for
  // every day earlier in the week than the start, and `%` in JavaScript keeps
  // the sign of the left operand.
  const sinceStart = (value.getDay() - weekStart + 7) % 7

  return addDays(value, -sinceStart)
}

/**
 * The inclusive range between two days.
 *
 * @param start - First day.
 * @param end - Last day.
 * @returns The range, as calendar days.
 */
function range(start: Date, end: Date): CalendarDateRange {
  return { startDate: toCalendarDate(start), endDate: toCalendarDate(end) }
}

/**
 * The most recently finished week, and the ones before it.
 *
 * **The week in progress is never returned.** A part-week compared against
 * whole ones reads as a collapse in volume, which is the single most expensive
 * misreading a weekly report can invite. `weekToDate` is how a reader asks for
 * the current week on purpose.
 *
 * @param reference - The day "now" falls on.
 * @param weekStart - The day the week begins.
 * @param back - 0 is the last completed week, 1 the week before it.
 * @returns That week, Saturday to Friday by default.
 */
export function completedWeek(
  reference: Date,
  weekStart: WeekStart,
  back = 0
): CalendarDateRange {
  const currentWeekStart = startOfWeek(reference, weekStart)
  const end = addDays(currentWeekStart, -1 - back * 7)

  return range(addDays(end, -6), end)
}

/**
 * A whole calendar month.
 *
 * @param year - The full year.
 * @param month - The month, 0 for January.
 * @returns The first to the last day of that month.
 */
export function calendarMonth(year: number, month: number): CalendarDateRange {
  // Day 0 of the next month is the last day of this one, which is how the
  // length of February stays `Date`'s problem rather than this module's.
  return range(new Date(year, month, 1), new Date(year, month + 1, 0))
}

/**
 * The last whole calendar month.
 *
 * @param reference - The day "now" falls on.
 * @returns The previous month, first day to last.
 */
export function previousCalendarMonth(reference: Date): CalendarDateRange {
  return calendarMonth(reference.getFullYear(), reference.getMonth() - 1)
}

/**
 * The calendar quarter a month belongs to, 0 for January–March.
 *
 * @param month - The month, 0 for January.
 * @returns The quarter index, 0 to 3.
 */
function quarterOf(month: number): number {
  return Math.floor(month / 3)
}

/**
 * The last whole calendar quarter.
 *
 * @param reference - The day "now" falls on.
 * @returns The previous quarter, first day to last.
 */
export function previousCalendarQuarter(reference: Date): CalendarDateRange {
  const firstMonth = quarterOf(reference.getMonth()) * 3 - 3
  const start = new Date(reference.getFullYear(), firstMonth, 1)

  return range(start, new Date(start.getFullYear(), start.getMonth() + 3, 0))
}

/**
 * The last whole calendar year.
 *
 * @param reference - The day "now" falls on.
 * @returns 1 January to 31 December of the previous year.
 */
export function lastCalendarYear(reference: Date): CalendarDateRange {
  const year = reference.getFullYear() - 1

  return range(new Date(year, 0, 1), new Date(year, 11, 31))
}

/**
 * The current week so far, up to and including `reference`.
 *
 * @param reference - The day "now" falls on.
 * @param weekStart - The day the week begins.
 * @returns The part-week to date.
 */
export function weekToDate(
  reference: Date,
  weekStart: WeekStart
): CalendarDateRange {
  return range(startOfWeek(reference, weekStart), reference)
}

/**
 * The current month so far, up to and including `reference`.
 *
 * @param reference - The day "now" falls on.
 * @returns The part-month to date.
 */
export function monthToDate(reference: Date): CalendarDateRange {
  return range(
    new Date(reference.getFullYear(), reference.getMonth(), 1),
    reference
  )
}

/**
 * The current quarter so far, up to and including `reference`.
 *
 * @param reference - The day "now" falls on.
 * @returns The part-quarter to date.
 */
export function quarterToDate(reference: Date): CalendarDateRange {
  return range(
    new Date(reference.getFullYear(), quarterOf(reference.getMonth()) * 3, 1),
    reference
  )
}

/**
 * The current year so far, up to and including `reference`.
 *
 * @param reference - The day "now" falls on.
 * @returns The part-year to date.
 */
export function yearToDate(reference: Date): CalendarDateRange {
  return range(new Date(reference.getFullYear(), 0, 1), reference)
}

/**
 * The last `days` days, ending on `reference`.
 *
 * Inclusive of `reference`, which is what the analytics screen's trailing
 * seven days already meant: seven days is today and the six before it, not
 * today and the seven before it.
 *
 * A count below one is treated as one. A range that ended before it started
 * would be sent to an endpoint that answers 400, and `reference` alone is the
 * nearest honest answer. `report-range.ts` states the same rule, because
 * `trailingDays` is this function with the clock supplied.
 *
 * @param days - How many days the window spans. One is `reference` alone.
 * @param reference - The day "now" falls on.
 * @returns The rolling window.
 */
export function rollingDays(days: number, reference: Date): CalendarDateRange {
  const span = Math.max(1, Math.floor(days))

  return range(addDays(reference, -(span - 1)), reference)
}

/**
 * The one civil day before `reference`.
 *
 * A saved alert named `yesterday` must mean a new day every morning. The
 * range is that day alone, inclusive, in the same local arithmetic as the
 * other presets.
 *
 * @param reference - The day "now" falls on.
 * @returns That previous day.
 */
export function previousDay(reference: Date): CalendarDateRange {
  const day = addDays(reference, -1)

  return range(day, day)
}
